import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../design-system/Card';
import { Button } from '../design-system/Button';
import { Input } from '../design-system/Input';
import { Textarea } from '../design-system/Textarea';
import { completeAthleteOnboarding } from '../../api/auth';
import { clsx } from 'clsx';

const AthleteOnboardingV2 = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  
  const [formData, setFormData] = useState({
    // Profile Info
    date_of_birth: '',
    gender: '',
    phone_number: '',
    profile_image: null,
    
    // Goals & Experience
    primary_goal: '',
    experience_level: '',
    sport_type: '',
    
    // Physical Stats
    height_cm: '',
    weight_kg: '',
    
    // Availability
    available_equipment: '',
    preferred_workout_days: [],
    preferred_workout_duration: 60,
    
    // Health
    injuries: '',
    medical_conditions: '',
  });

  const steps = [
    {
      id: 'profile',
      title: "Let's complete your profile",
      subtitle: 'This helps us personalize your experience',
      type: 'form',
    },
    {
      id: 'goals',
      title: "What's your primary fitness goal?",
      subtitle: 'Choose the one that matters most to you',
      field: 'primary_goal',
      options: [
        {
          value: 'MUSCLE_GAIN',
          label: 'Build Muscle',
          icon: '💪',
          description: 'Increase muscle mass and size',
        },
        {
          value: 'WEIGHT_LOSS',
          label: 'Lose Weight',
          icon: '🏃',
          description: 'Reduce body fat and get leaner',
        },
        {
          value: 'STRENGTH',
          label: 'Get Stronger',
          icon: '🏋️',
          description: 'Increase your lifting numbers',
        },
        {
          value: 'ENDURANCE',
          label: 'Improve Endurance',
          icon: '🏃‍♂️',
          description: 'Better cardiovascular fitness',
        },
        {
          value: 'GENERAL_FITNESS',
          label: 'General Fitness',
          icon: '🌟',
          description: 'Overall health and wellness',
        },
      ],
    },
    {
      id: 'sport',
      title: 'What sport or activity do you focus on?',
      subtitle: 'This helps us tailor your training',
      field: 'sport_type',
      options: [
        { value: 'GYM', label: 'Gym Training', icon: '🏋️‍♂️' },
        { value: 'RUNNING', label: 'Running', icon: '🏃‍♂️' },
        { value: 'CYCLING', label: 'Cycling', icon: '🚴‍♂️' },
        { value: 'SWIMMING', label: 'Swimming', icon: '🏊‍♂️' },
        { value: 'TEAM_SPORTS', label: 'Team Sports', icon: '⚽' },
        { value: 'MARTIAL_ARTS', label: 'Martial Arts', icon: '🥋' },
        { value: 'YOGA', label: 'Yoga/Pilates', icon: '🧘‍♂️' },
        { value: 'OTHER', label: 'Other', icon: '🎯' },
      ],
    },
    {
      id: 'experience',
      title: "What's your training experience?",
      subtitle: 'Be honest - this helps us start at the right level',
      field: 'experience_level',
      options: [
        {
          value: 'BEGINNER',
          label: 'Beginner',
          icon: '🌱',
          description: 'New to training (0-1 years)',
        },
        {
          value: 'INTERMEDIATE',
          label: 'Intermediate',
          icon: '📈',
          description: 'Some experience (1-3 years)',
        },
        {
          value: 'ADVANCED',
          label: 'Advanced',
          icon: '🚀',
          description: 'Very experienced (3+ years)',
        },
      ],
    },
    {
      id: 'schedule',
      title: 'When can you workout?',
      subtitle: 'Select all the days that work for you',
      field: 'preferred_workout_days',
      type: 'multi-select',
      options: [
        { value: 'monday', label: 'Mon', fullLabel: 'Monday' },
        { value: 'tuesday', label: 'Tue', fullLabel: 'Tuesday' },
        { value: 'wednesday', label: 'Wed', fullLabel: 'Wednesday' },
        { value: 'thursday', label: 'Thu', fullLabel: 'Thursday' },
        { value: 'friday', label: 'Fri', fullLabel: 'Friday' },
        { value: 'saturday', label: 'Sat', fullLabel: 'Saturday' },
        { value: 'sunday', label: 'Sun', fullLabel: 'Sunday' },
      ],
    },
  ];

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOptionSelect = (value) => {
    const currentStepData = steps[currentStep];
    
    if (currentStepData.type === 'multi-select') {
      const currentValues = formData[currentStepData.field] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      
      handleFormChange(currentStepData.field, newValues);
    } else if (currentStepData.field) {
      handleFormChange(currentStepData.field, value);
      // Auto-advance for single select
      setTimeout(() => {
        if (currentStep < steps.length - 1) {
          setCurrentStep(currentStep + 1);
        }
      }, 300);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'preferred_workout_days' && Array.isArray(formData[key])) {
          formData[key].forEach(day => submitData.append('preferred_workout_days', day));
        } else if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });
      
      // Append profile image if exists
      if (profileImage) {
        submitData.append('profile_image', profileImage);
      }
      
      await completeAthleteOnboarding(submitData);
      navigate('/onboarding/success');
    } catch (error) {
      console.error('Onboarding error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  // Render profile form step
  const renderProfileForm = () => (
    <div className="space-y-6">
      {/* Profile Photo Upload */}
      <div className="flex flex-col items-center">
        <div className="relative group">
          <div className={clsx(
            'w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-gray-200 cursor-pointer transition-all',
            'group-hover:border-[#4B5FDC]'
          )}>
            {profileImagePreview ? (
              <img 
                src={profileImagePreview} 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="absolute bottom-0 right-0 bg-[#4B5FDC] text-white rounded-full p-2 shadow-lg hover:bg-[#3A4BC9] transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
        <p className="text-sm text-gray-500 mt-2">Add a profile photo</p>
      </div>

      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Date of Birth"
          type="date"
          value={formData.date_of_birth}
          onChange={(e) => handleFormChange('date_of_birth', e.target.value)}
          max={new Date().toISOString().split('T')[0]}
        />
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
          <select
            value={formData.gender}
            onChange={(e) => handleFormChange('gender', e.target.value)}
            className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-[#4B5FDC] focus:outline-none transition-all"
          >
            <option value="">Select gender</option>
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
            <option value="OTHER">Other</option>
            <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
          </select>
        </div>
      </div>

      <Input
        label="Phone Number"
        type="tel"
        value={formData.phone_number}
        onChange={(e) => handleFormChange('phone_number', e.target.value)}
        placeholder="+1 (555) 123-4567"
        hint="Optional - for important updates only"
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Height"
          type="number"
          value={formData.height_cm}
          onChange={(e) => handleFormChange('height_cm', e.target.value)}
          placeholder="170"
          rightIcon={<span className="text-gray-500">cm</span>}
        />
        
        <Input
          label="Weight"
          type="number"
          value={formData.weight_kg}
          onChange={(e) => handleFormChange('weight_kg', e.target.value)}
          placeholder="70"
          rightIcon={<span className="text-gray-500">kg</span>}
        />
      </div>
    </div>
  );

  // Render option selection
  const renderOptions = () => (
    <div className="space-y-3">
      {currentStepData.options?.map((option) => {
        const isSelected = currentStepData.type === 'multi-select'
          ? (formData[currentStepData.field] || []).includes(option.value)
          : formData[currentStepData.field] === option.value;

        return (
          <Card
            key={option.value}
            variant={isSelected ? 'highlighted' : 'interactive'}
            onClick={() => handleOptionSelect(option.value)}
            className={clsx(
              'transition-all cursor-pointer',
              isSelected && 'ring-2 ring-[#4B5FDC] ring-offset-2'
            )}
          >
            <div className="flex items-center">
              {currentStepData.type === 'multi-select' && (
                <div className={clsx(
                  'w-5 h-5 rounded border-2 mr-4 flex items-center justify-center transition-all',
                  isSelected
                    ? 'border-[#4B5FDC] bg-[#4B5FDC]'
                    : 'border-gray-300'
                )}>
                  {isSelected && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              )}
              
              {option.icon && (
                <div className="text-3xl mr-4">{option.icon}</div>
              )}
              
              <div className="flex-1">
                <div className="font-semibold text-gray-900">
                  {option.fullLabel || option.label}
                </div>
                {option.description && (
                  <div className="text-sm text-gray-600 mt-1">
                    {option.description}
                  </div>
                )}
              </div>
              
              {currentStepData.type !== 'multi-select' && isSelected && (
                <div className="text-[#4B5FDC]">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );

  // Render days grid for schedule
  const renderScheduleGrid = () => (
    <div className="grid grid-cols-7 gap-2">
      {currentStepData.options?.map((day) => {
        const isSelected = (formData.preferred_workout_days || []).includes(day.value);
        
        return (
          <button
            key={day.value}
            onClick={() => handleOptionSelect(day.value)}
            className={clsx(
              'aspect-square rounded-lg font-medium transition-all',
              'flex items-center justify-center text-sm',
              isSelected
                ? 'bg-[#4B5FDC] text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            {day.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Welcome to FitGenie!</h2>
            <span className="text-sm text-gray-500">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-[#4B5FDC] h-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <Card className="animate-slideUp">
          <div className="p-2">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {currentStepData.title}
            </h3>
            {currentStepData.subtitle && (
              <p className="text-gray-600 mb-6">{currentStepData.subtitle}</p>
            )}

            {/* Render appropriate content based on step type */}
            {currentStepData.type === 'form' && renderProfileForm()}
            {currentStepData.id === 'schedule' ? renderScheduleGrid() : 
             currentStepData.options && currentStepData.type !== 'form' && renderOptions()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentStep === 0}
            >
              Back
            </Button>

            <div className="flex gap-2">
              {[...Array(steps.length)].map((_, index) => (
                <div
                  key={index}
                  className={clsx(
                    'w-2 h-2 rounded-full transition-all',
                    index === currentStep
                      ? 'w-8 bg-[#4B5FDC]'
                      : index < currentStep
                      ? 'bg-[#4B5FDC]'
                      : 'bg-gray-300'
                  )}
                />
              ))}
            </div>

            {(currentStepData.type === 'form' || 
              currentStepData.type === 'multi-select' ||
              currentStepData.id === 'schedule') && (
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={loading}
                isLoading={loading}
                haptic
              >
                {currentStep === steps.length - 1 ? 'Complete' : 'Next'}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AthleteOnboardingV2;